/**
 * Cuenta contable (spec/03-data-model.md §2, spec/01-requirements.md FR-ACCT-*).
 * Las cuentas se crean manualmente; nunca se descubren automaticamente.
 */

export type AccountType = "asset" | "liability" | "equity" | "income" | "expense";

const VALID_TYPES: readonly AccountType[] = ["asset", "liability", "equity", "income", "expense"];

export interface Account {
  readonly accountId: string;
  readonly name: string;
  readonly type: AccountType;
  readonly currency: string;
  readonly parentId: string | null;
  readonly archived: boolean;
  readonly instrumentId: string | null;
}

export interface CreateAccountInput {
  accountId: string;
  name: string;
  type: AccountType;
  currency: string;
  parentId?: string | null;
  instrumentId?: string | null;
}

export function isValidAccountType(type: string): type is AccountType {
  return (VALID_TYPES as readonly string[]).includes(type);
}

export class InvalidAccountTypeError extends Error {
  constructor(type: string) {
    super(`Tipo de cuenta invalido: ${type}. Debe ser uno de: ${VALID_TYPES.join(", ")}`);
    this.name = "InvalidAccountTypeError";
  }
}

export function createAccount(input: CreateAccountInput): Account {
  if (!isValidAccountType(input.type)) {
    throw new InvalidAccountTypeError(input.type);
  }
  if (!input.name.trim()) {
    throw new RangeError("El nombre de la cuenta no puede estar vacio");
  }
  return {
    accountId: input.accountId,
    name: input.name,
    type: input.type,
    currency: input.currency,
    parentId: input.parentId ?? null,
    archived: false,
    instrumentId: input.instrumentId ?? null,
  };
}

/** Renombrar preserva la identidad (FR-ACCT-7): mismo accountId, nuevo name. */
export function renameAccount(account: Account, newName: string): Account {
  if (!newName.trim()) {
    throw new RangeError("El nuevo nombre no puede estar vacio");
  }
  return { ...account, name: newName };
}

/** Archivar es la unica forma de "eliminar" una cuenta (FR-ACCT-5): nunca se borra. */
export function archiveAccount(account: Account): Account {
  return { ...account, archived: true };
}

/**
 * Detecta ciclos en la jerarquia de cuentas (FR-ACCT-3: sin ciclos).
 * `resolveParent` debe devolver la cuenta padre o undefined si no existe.
 */
export function hasCycle(
  accountId: string,
  resolveParent: (id: string) => Account | undefined,
): boolean {
  const seen = new Set<string>();
  let current: string | null = accountId;
  while (current !== null) {
    if (seen.has(current)) return true;
    seen.add(current);
    const parent = resolveParent(current);
    current = parent?.parentId ?? null;
  }
  return false;
}
